import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, XCircle, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useLang } from "../context/LangContext";
import { usersAPI, duelAPI, statsAPI } from "../api";

interface ProgressSummary {
  totalSessions: number;
  completedSessions: number;
  totalAnswered: number;
  totalCorrect: number;
  totalWrong: number;
  avgScore: number;
  favoritesCount: number;
}

interface RecentSession {
  sessionId: string;
  mode: string;
  status: string;
  totalQuestions: number;
  correctCount: number;
  wrongCount: number;
  score_percent: number;
  createdAt: string;
}

interface DuelStats {
  totalGames: number;
  wins: number;
  losses: number;
  draws: number;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 14,
      padding: 20,
      marginBottom: 12,
    }}>
    <p style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
      {title}
    </p>
    {children}
  </div>
);

const Tile: React.FC<{ label: string; value: string | number; color?: string }> = ({
  label,
  value,
  color = "#3B82F6",
}) => (
  <div
    style={{
      flex: "1 1 120px",
      background: "var(--card2)",
      border: "1px solid var(--border)",
      borderRadius: 12,
      padding: 14,
      textAlign: "center",
    }}>
    <p style={{ color, fontWeight: 800, fontSize: 20 }}>{value}</p>
    <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 4 }}>{label}</p>
  </div>
);

const StatisticsPage: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [duelStats, setDuelStats] = useState<DuelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([usersAPI.getProgress(), duelAPI.getStats()])
      .then(([progressRes, duelRes]) => {
        const d = progressRes.data?.data || progressRes.data || {};
        setSummary(d.summary || null);
        setRecentSessions(Array.isArray(d.recentSessions) ? d.recentSessions : []);
        setDuelStats(duelRes.data || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClearHistory = async () => {
    setClearing(true);
    try {
      await statsAPI.clearHistory();
      toast.success(t("clearHistorySuccess"));
      setConfirmOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Xatolik yuz berdi");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/settings")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {t("statistics")}
          </span>
        </div>
      </div>

      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "68px 16px 32px" }}>
        {loading ?
          <p style={{ color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
            {t("loading")}
          </p>
        : <>
            <Section title={t("statistics")}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Tile label={t("totalTests")} value={summary?.totalSessions ?? 0} />
                <Tile
                  label={t("avgScore")}
                  value={`${Math.round(summary?.avgScore ?? 0)}%`}
                  color="#22C55E"
                />
                <Tile
                  label={t("correctAnswers")}
                  value={summary?.totalCorrect ?? 0}
                  color="#22C55E"
                />
                <Tile
                  label={t("wrongAnswers")}
                  value={summary?.totalWrong ?? 0}
                  color="#EF4444"
                />
                <Tile
                  label={t("favoritesCount")}
                  value={summary?.favoritesCount ?? 0}
                  color="#F59E0B"
                />
              </div>
            </Section>

            {recentSessions.length > 0 && (
              <Section title={t("recentTests")}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {recentSessions.slice(0, 5).map((s) => (
                    <div
                      key={s.sessionId}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 12px",
                        background: "var(--card2)",
                        borderRadius: 10,
                      }}>
                      <div>
                        <p style={{ color: "#E2E8F0", fontSize: 12.5, fontWeight: 600 }}>
                          {s.mode} · {s.totalQuestions} savol
                        </p>
                        <p style={{ color: "#475569", fontSize: 11, marginTop: 2 }}>
                          {new Date(s.createdAt).toLocaleDateString("uz-UZ")}
                        </p>
                      </div>
                      <span
                        style={{
                          color: s.score_percent >= 80 ? "#22C55E" : "#F59E0B",
                          fontWeight: 800,
                          fontSize: 14,
                        }}>
                        {Math.round(s.score_percent)}%
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section title={t("duelStats")}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Tile label={t("wins")} value={duelStats?.wins ?? 0} color="#22C55E" />
                <Tile label={t("losses")} value={duelStats?.losses ?? 0} color="#EF4444" />
                <Tile label={t("draws")} value={duelStats?.draws ?? 0} color="#64748B" />
                <Tile label={t("totalGames")} value={duelStats?.totalGames ?? 0} />
              </div>
            </Section>

            {/* DANGER ZONE */}
            <div
              style={{
                background: "rgba(239,68,68,.05)",
                border: "1px solid rgba(239,68,68,.3)",
                borderRadius: 14,
                padding: 20,
                marginTop: 20,
              }}>
              <p style={{ color: "#EF4444", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                {t("dangerZone")}
              </p>
              <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 14 }}>
                {t("clearHistoryDesc")}
              </p>
              <button
                onClick={() => setConfirmOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "11px 18px",
                  borderRadius: 10,
                  border: "1px solid rgba(239,68,68,.3)",
                  background: "rgba(239,68,68,.1)",
                  color: "#EF4444",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}>
                <Trash2 size={15} /> {t("clearHistory")}
              </button>
            </div>
          </>
        }
      </div>

      {confirmOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}>
          <div
            style={{
              background: "#111827",
              border: "1px solid #1E2D45",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 340,
            }}>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <XCircle size={36} style={{ color: "#EF4444", margin: "0 auto 10px" }} />
              <p style={{ color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 8 }}>
                {t("clearHistory")}
              </p>
              <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
                {t("clearHistoryConfirm")}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={clearing}
                style={{
                  flex: 1,
                  padding: "11px",
                  borderRadius: 10,
                  border: "1px solid #1E2D45",
                  background: "transparent",
                  color: "#E2E8F0",
                  fontWeight: 700,
                  cursor: clearing ? "not-allowed" : "pointer",
                }}>
                {t("cancel")}
              </button>
              <button
                onClick={handleClearHistory}
                disabled={clearing}
                style={{
                  flex: 1,
                  padding: "11px",
                  borderRadius: 10,
                  border: "none",
                  background: "#EF4444",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: clearing ? "not-allowed" : "pointer",
                  opacity: clearing ? 0.7 : 1,
                }}>
                {clearing ? "..." : t("clearHistory")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPage;
